import { useCallback, useMemo, useRef } from 'react';

type usePartialFormModuleProps<TFormData extends Record<string, unknown>> = {
  numberOfPartialForms: number;
  values: TFormData | undefined;
  parentOnChange: (moduleName: string, data: TFormData) => void;
  parentOnValidation: (moduleName: string, isValid: boolean) => void;
};

/**
 * Hook to help split a module into several forms.
 *
 * Adaps the onChange- and onValidation-handlers to work when splitting the module into several
 * forms.
 *
 * Useful for doing partial validation in a module and/or organizing the code into smaller pieces.
 */
export const usePartialFormModule = <TFormData extends Record<string, unknown>>({
  numberOfPartialForms,
  values,
  parentOnChange,
  parentOnValidation,
}: usePartialFormModuleProps<TFormData>) => {
  const formState = useRef({
    data: values ?? ({} as TFormData),
    isValid: Array.from({ length: numberOfPartialForms }, () => false),
  });

  const onChange = useCallback(
    (moduleName: string, data: Record<string, unknown>) => {
      Object.assign(formState.current.data, data);
      parentOnChange(moduleName, formState.current.data);
    },
    [parentOnChange]
  );

  const validationHandlers = useMemo(() => {
    return Array.from({ length: numberOfPartialForms }, (_, index) => {
      return (moduleName: string, isValid: boolean) => {
        formState.current.isValid[index] = isValid;
        parentOnValidation(
          moduleName,
          !Object.values(formState.current.isValid).some((isValid) => !isValid)
        );
      };
    });
  }, [parentOnValidation, numberOfPartialForms]);

  return { onChange, validationHandlers };
};
